import { HttpError } from 'ajaxian';
import { just, Maybe, nothing } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import AsyncSelect from 'react-select/async';
import { components } from 'react-select';
import { Task } from 'taskarian';
import { isMulti, placeholder, prepOnChange, Value } from '..';
import { callApi, MissingApiCompatibility, MissingApplicationId } from '../../../../../Appy';
import { findLinkT, MissingLinkError } from '../../../../../LinkyLinky';
import { Link, Rel } from '../../../../Types';
import { selectionOptionResourceDecoder } from '../../../Decoders/SelectionOptionDecoders';
import { isReadonly, SelectionOption, SelectionsField } from '../../../Types';
import { createCustomOption } from '../CustomOption';

interface Props {
  field: SelectionsField;
  onChange: (value: Value) => void;
  optionsRel: Rel;
  dataIntercomTarget?: string;
}

const fetchOptions = callApi(selectionOptionResourceDecoder, {});

type Options = { options: ReadonlyArray<SelectionOption> };

type LoadError = MissingApplicationId | MissingApiCompatibility | MissingLinkError | HttpError;

const loadOptions = (field: SelectionsField, optionsRel: Rel): Task<LoadError, Options> =>
  Task.succeed<LoadError, ReadonlyArray<Link>>(field.links)
    .andThen(findLinkT(optionsRel))
    .andThen(fetchOptions)
    .map((r) => ({
      options: r.payload,
    }));

const Asynchronous: React.FC<Props> = ({ field, onChange, optionsRel, dataIntercomTarget }) => {
  const loadOptionsWithCache = React.useCallback(() => {
    let cache: Maybe<{ options: ReadonlyArray<SelectionOption> }> = nothing();

    return cache
      .map((c) => Task.succeed(c).resolve())
      .getOrElse(() =>
        loadOptions(field, optionsRel)
          .do((c) => (cache = just(c)))
          .resolve(),
      )
      .then((result) => result.options);
  }, [field, optionsRel]);

  const CustomOption = React.useMemo(
    () => (dataIntercomTarget ? createCustomOption(dataIntercomTarget) : components.Option),
    [dataIntercomTarget],
  );

  return (
    <AsyncSelect
      cacheOptions
      defaultOptions
      loadOptions={loadOptionsWithCache}
      value={field.value}
      className={'mt-1 block w-48 bg-white px-3 py-2 text-sm font-medium'}
      isMulti={isMulti(field)}
      isDisabled={isReadonly(field)}
      placeholder={placeholder(field)}
      name={field.name}
      onChange={prepOnChange(onChange)}
      components={{
        Option: CustomOption,
      }}
    />
  );
};

export default observer(Asynchronous);
